import {
  BenchEntryDTO,
  NoteDTO,
  OfferDTO,
} from '../../../shared/model/bench-entry.model';

/**
 * NP20-1229: Einzel-Speichern eines Bereichs der Detailansicht. Der aktuelle Stand wird frisch
 * geladen, darin nur der eigene Bereich ersetzt und per PUT gespeichert. Die Funktionen hier
 * vergleichen Grundstand, lokalen Stand und frischen Stand.
 */

type Text = string | null | undefined;

/** Wie im Backend: null, undefined und "" gelten als gleich. */
export const sameText = (a: Text, b: Text): boolean => (a ?? '') === (b ?? '');

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const hasText = (note: NoteDTO): boolean => !!note.text?.trim();

const withIds = <T extends { id: string | null }>(items: T[]): Map<string, T> =>
  new Map(
    (items ?? [])
      .filter((item): item is T & { id: string } => !!item.id)
      .map((item) => [item.id, item]),
  );

export function noteEquals(a: NoteDTO, b: NoteDTO): boolean {
  return (
    sameText(a.id, b.id) &&
    sameText(a.date, b.date) &&
    sameText(a.author, b.author) &&
    sameText(a.text, b.text) &&
    !!a.isMeetingNote === !!b.isMeetingNote
  );
}

interface OwnNoteChanges {
  edited: Map<string, NoteDTO>;
  deleted: Set<string>;
}

/** Eigene Bearbeitungen gespeicherter Bemerkungen: lokal geändert, geleert oder entfernt. */
function ownNoteChanges(base: NoteDTO[], local: NoteDTO[]): OwnNoteChanges {
  const localById = withIds(local);
  const edited = new Map<string, NoteDTO>();
  const deleted = new Set<string>();
  for (const [id, baseNote] of withIds(base)) {
    const localNote = localById.get(id);
    if (!localNote || !hasText(localNote)) deleted.add(id);
    else if (!noteEquals(localNote, baseNote)) edited.set(id, localNote);
  }
  return { edited, deleted };
}

/** Konflikt nur, wenn eine selbst geänderte oder gelöschte Bemerkung auf dem Server abweicht oder fehlt. */
export function notesConflict(
  base: NoteDTO[],
  local: NoteDTO[],
  fresh: NoteDTO[],
): boolean {
  const { edited, deleted } = ownNoteChanges(base, local);
  const baseById = withIds(base);
  const freshById = withIds(fresh);
  return [...edited.keys(), ...deleted].some((id) => {
    const freshNote = freshById.get(id);
    return !freshNote || !noteEquals(freshNote, baseById.get(id)!);
  });
}

const normalizedNote = (note: NoteDTO): NoteDTO => ({
  ...note,
  date: note.date ? note.date : null,
});

/**
 * Liste des frischen Stands mit den eigenen Änderungen: gelöschte heraus, geänderte per id
 * ersetzt, neue in lokaler Reihenfolge angehängt. Leere Bemerkungen fallen weg wie beim
 * Speichern des ganzen Eintrags.
 */
export function mergeNotes(
  base: NoteDTO[],
  local: NoteDTO[],
  fresh: NoteDTO[],
): NoteDTO[] {
  const { edited, deleted } = ownNoteChanges(base, local);
  const kept = (fresh ?? [])
    .filter((note) => !(note.id && deleted.has(note.id)))
    .map((note) => (note.id && edited.get(note.id)) || note);
  const added = (local ?? []).filter((note) => !note.id);
  return [...kept, ...added].filter(hasText).map(normalizedNote);
}

/** Frischer Stand unverändert, nur die Bemerkungen zum Berater zusammengeführt. */
export function notesPayload(
  fresh: BenchEntryDTO,
  base: NoteDTO[],
  local: NoteDTO[],
): BenchEntryDTO {
  return { ...clone(fresh), notes: mergeNotes(base, local, fresh.notes) };
}

/**
 * Ersetzt den Inhalt eines vorhandenen Objekts in der Schlüsselreihenfolge der Quelle. So bleiben
 * Objekt-Referenzen (aufgeklappte Angebote, Fokus) erhalten und `JSON.stringify` vergleicht gleich.
 */
export function replaceContent<T extends object>(target: T, source: T): T {
  const record = target as Record<string, unknown>;
  for (const key of Object.keys(record)) delete record[key];
  Object.assign(record, clone(source));
  return target;
}

/** Neu (ohne id), im Grundstand unbekannt oder abweichend. */
export function isOfferChanged(
  offer: OfferDTO,
  baseOffers: Map<string, OfferDTO>,
): boolean {
  if (!offer.id) return true;
  const baseOffer = baseOffers.get(offer.id);
  return !baseOffer || JSON.stringify(baseOffer) !== JSON.stringify(offer);
}

export interface AdoptedSection {
  entry: BenchEntryDTO;
  base: BenchEntryDTO;
  /** Entfernte und lokal unveränderte Angebote, die auf dem Server fehlen. */
  dropped: OfferDTO[];
}

const TEXT_FIELDS = [
  'date',
  'referenceNumber',
  'externalReferenceNumber',
  'role',
  'customer',
  'intermediary',
  'businessUnitId',
  'status',
  'priority',
  'commitmentFrom',
  'extendedUntil',
  'projectStart',
  'projectEnd',
  'volumeUnit',
  'interviewPdl',
  'interviewCustomer',
  'hubspotUrl',
] as const satisfies readonly (keyof OfferDTO)[];

const NUMBER_FIELDS = [
  'probability',
  'volume',
  'hourlyRateRemote',
  'hourlyRateOnSite',
] as const satisfies readonly (keyof OfferDTO)[];

const sameNumber = (
  a: number | null | undefined,
  b: number | null | undefined,
): boolean => (a ?? null) === (b ?? null);

/** Wie im Backend: die Kürzel zählen nur, solange keine Person verknüpft ist. */
const sameSubmitter = (a: OfferDTO, b: OfferDTO): boolean =>
  a.submittedByUserId || b.submittedByUserId
    ? sameText(a.submittedByUserId, b.submittedByUserId)
    : sameText(a.submittedBy, b.submittedBy);

const sameNotes = (a: NoteDTO[], b: NoteDTO[]): boolean =>
  (a ?? []).length === (b ?? []).length &&
  (a ?? []).every((note, index) => noteEquals(note, b[index]));

/** Vergleicht, was der PUT schreibt; Dateimetadaten des Anhangs setzt allein der Upload. */
function sameOfferContent(a: OfferDTO, b: OfferDTO): boolean {
  return (
    TEXT_FIELDS.every((field) => sameText(a[field], b[field])) &&
    NUMBER_FIELDS.every((field) => sameNumber(a[field], b[field])) &&
    !!a.extension === !!b.extension &&
    sameSubmitter(a, b) &&
    sameText(a.attachment?.note, b.attachment?.note) &&
    sameNotes(a.notes, b.notes)
  );
}

/**
 * Konflikt am eigenen Angebot: Es fehlt auf dem Server oder weicht dort vom Grundstand ab. Ein
 * neues Angebot kann keinen Konflikt haben.
 */
export function offerConflicts(
  offer: OfferDTO,
  baseOffers: OfferDTO[],
  freshOffers: OfferDTO[],
): boolean {
  if (!offer.id) return false;
  const freshOffer = withIds(freshOffers).get(offer.id);
  if (!freshOffer) return true;
  const baseOffer = withIds(baseOffers).get(offer.id);
  return !!baseOffer && !sameOfferContent(baseOffer, freshOffer);
}

/** Ersetzt das Angebot per id oder hängt ein neues an; der übergebene Stand bleibt unverändert. */
export function withOffer(
  entry: BenchEntryDTO,
  offer: OfferDTO,
): BenchEntryDTO {
  const copy = clone(entry);
  const index = offer.id
    ? copy.offers.findIndex((candidate) => candidate.id === offer.id)
    : -1;
  if (index >= 0) copy.offers[index] = clone(offer);
  else copy.offers.push(clone(offer));
  return copy;
}

const emptyToNull = (value: string | null): string | null =>
  value ? value : null;

/** Dieselben Bereinigungen wie beim Speichern des ganzen Eintrags. */
export function normalizedOffer(
  offer: OfferDTO,
  applyChangesToProject: boolean,
): OfferDTO {
  const copy = clone(offer);
  copy.date = emptyToNull(copy.date);
  copy.notes = (copy.notes ?? []).filter(hasText).map(normalizedNote);
  // Eine Notiz aus reinen Leerzeichen ist keine Notiz.
  if (copy.attachment) {
    copy.attachment.note = copy.attachment.note?.trim()
      ? copy.attachment.note
      : null;
  }
  if (applyChangesToProject) copy.applyChangesToProject = true;
  else delete copy.applyChangesToProject;
  return copy;
}

/** Frischer Stand unverändert, nur das eigene Angebot ersetzt oder angehängt. */
export function offerPayload(
  fresh: BenchEntryDTO,
  offer: OfferDTO,
  applyChangesToProject: boolean,
): BenchEntryDTO {
  return withOffer(fresh, normalizedOffer(offer, applyChangesToProject));
}

/** Ein neues Angebot ist das, dessen id in der Antwort, aber nicht im frischen Stand steht. */
export function findSavedOffer(
  fresh: BenchEntryDTO,
  response: BenchEntryDTO,
  offer: OfferDTO,
): OfferDTO | undefined {
  const responseOffers = response.offers ?? [];
  if (offer.id)
    return responseOffers.find((candidate) => candidate.id === offer.id);
  const freshIds = withIds(fresh.offers);
  return responseOffers.find(
    (candidate) => !!candidate.id && !freshIds.has(candidate.id),
  );
}

export function withoutOffer(
  entry: BenchEntryDTO,
  offerId: string,
): BenchEntryDTO {
  const copy = clone(entry);
  copy.offers = copy.offers.filter((offer) => offer.id !== offerId);
  return copy;
}

/** Bereich, den das Einzel-Speichern geschrieben hat. */
export type SavedSection =
  | { kind: 'notes' }
  | { kind: 'offer'; offer: OfferDTO; fresh: BenchEntryDTO }
  | { kind: 'remove'; offer: OfferDTO };

/**
 * Übernimmt die Antwort nach einem Einzel-Speichern. Felder des Eintrags kommen aus der Antwort,
 * ebenso der gespeicherte Bereich und lokal unveränderte Angebote. Lokal geänderte Bereiche
 * bleiben im Formular; ihr Grundstand bleibt der alte, damit sie weiter als ungespeichert gelten.
 */
export function adoptSectionResponse(
  local: BenchEntryDTO,
  base: BenchEntryDTO,
  response: BenchEntryDTO,
  saved: SavedSection = { kind: 'notes' },
): AdoptedSection {
  const baseOffers = withIds(base.offers);
  const responseOffers = withIds(response.offers);
  const localIds = new Set(
    local.offers.map((offer) => offer.id).filter((id) => !!id),
  );
  const offers: OfferDTO[] = [];
  const baseList: OfferDTO[] = [];
  const dropped: OfferDTO[] = [];
  const changedIds = new Set(
    local.offers
      .filter(
        (offer) =>
          !!offer.id &&
          !(saved.kind !== 'notes' && offer === saved.offer) &&
          isOfferChanged(offer, baseOffers),
      )
      .map((offer) => offer.id!),
  );

  for (const offer of local.offers) {
    if (saved.kind === 'remove' && offer === saved.offer) {
      dropped.push(offer);
      continue;
    }
    const savedOffer =
      saved.kind === 'offer' && offer === saved.offer
        ? findSavedOffer(saved.fresh, response, offer)
        : undefined;
    if (savedOffer?.id) {
      localIds.add(savedOffer.id);
      offers.push(replaceContent(offer, savedOffer));
      baseList.push(clone(savedOffer));
      continue;
    }
    if (isOfferChanged(offer, baseOffers)) {
      offers.push(offer);
      const baseOffer = offer.id ? baseOffers.get(offer.id) : undefined;
      if (baseOffer) baseList.push(clone(baseOffer));
      continue;
    }
    const serverOffer = responseOffers.get(offer.id!);
    if (!serverOffer) {
      dropped.push(offer);
      continue;
    }
    offers.push(replaceContent(offer, serverOffer));
    baseList.push(clone(serverOffer));
  }

  for (const serverOffer of response.offers ?? []) {
    if (serverOffer.id && localIds.has(serverOffer.id)) continue;
    const removedLocally = serverOffer.id
      ? baseOffers.get(serverOffer.id)
      : undefined;
    if (removedLocally) {
      baseList.push(clone(removedLocally));
      continue;
    }
    offers.push(clone(serverOffer));
    baseList.push(clone(serverOffer));
  }

  // Haben andere ein selbst geändertes Angebot geändert oder gelöscht oder ein selbst entferntes
  // geändert, bleibt die alte version: „Alles speichern“ läuft dann in den Konflikt.
  const differsFromBase = (id: string): boolean =>
    JSON.stringify(responseOffers.get(id)) !==
    JSON.stringify(baseOffers.get(id));
  const changedByOthers =
    [...changedIds].some((id) => baseOffers.has(id) && differsFromBase(id)) ||
    base.offers.some(
      (offer) =>
        !!offer.id &&
        !localIds.has(offer.id) &&
        responseOffers.has(offer.id) &&
        differsFromBase(offer.id),
    );
  const version = changedByOthers ? base.version : response.version;

  const notesOpen =
    saved.kind !== 'notes' &&
    JSON.stringify(local.notes) !== JSON.stringify(base.notes);
  const adopted: AdoptedSection = {
    entry: { ...clone(response), version, offers },
    base: { ...clone(response), version, offers: baseList },
    dropped,
  };
  if (notesOpen) {
    adopted.entry.notes = local.notes;
    adopted.base.notes = clone(base.notes);
  }
  return adopted;
}
